/**
 * Admin API — work types domain.
 *
 * "Work type" is what kind of work a service is, in shared terms partners
 * understand. Two surfaces consume this module:
 *   - the platform admin Work Types screen (the /admin/work-types endpoints,
 *     system-admin only), and
 *   - the location services tab (labelling a service with a work type and the
 *     account-level progress counts).
 */

import { webApiClient } from '../apiClient';
import { dedup, extractItems } from './helpers';

/** One term of the shared work-type vocabulary. */
export interface WorkTypeTerm {
  /** Dotted, readable, immutable key — e.g. "pdr.dent.medium". */
  key: string;
  parent_key?: string;
  label: string;
  aliases: string[];
  status: 'active' | 'deprecated';
}

/** How a service relates to the shared work-type list. */
export type ServiceWorkTypeState = 'unlabelled' | 'suggested' | 'confirmed' | 'excluded';

export interface WorkTypeProgress {
  unlabelled: number;
  suggested: number;
  confirmed: number;
  excluded: number;
}

/**
 * Throw-on-error with the backend's structured message when present.
 * The generic `unwrap` loses the response body; these endpoints return
 * useful {error, code} bodies on 400/403/409 that the UI should surface.
 */
async function unwrapWithBody<T>(pending: Promise<any>, fallbackMessage: string): Promise<T> {
  const res = await pending;
  if (!res.success) {
    const body: any = res.errorBody ?? null;
    const err: Error & { status?: number; code?: string } = new Error(body?.error || body?.message || res.error || fallbackMessage);
    err.status = res.status;
    if (body?.code) err.code = body.code;
    throw err;
  }
  return res.data as T;
}

const normalizeTerm = (t: any): WorkTypeTerm => ({
  key: String(t.key ?? ''),
  parent_key: t.parent_key || undefined,
  label: String(t.label ?? t.key ?? ''),
  aliases: Array.isArray(t.aliases) ? t.aliases.map(String) : [],
  status: t.status === 'deprecated' ? 'deprecated' : 'active',
});

/** Active terms only — what the services-tab picker offers. */
export async function listWorkTypes(): Promise<WorkTypeTerm[]> {
  return dedup(`listWorkTypes:${webApiClient.getBaseUrl()}`, async () => {
    const json = await unwrapWithBody<any>(webApiClient.get<any>('/work-types'), 'Failed to load work types');
    return extractItems<any>(json, ['work_types', 'terms']).map(normalizeTerm);
  });
}

/** Full vocabulary including deprecated terms — the admin screen. */
export async function listAllWorkTypes(): Promise<WorkTypeTerm[]> {
  const json = await unwrapWithBody<any>(webApiClient.get<any>('/admin/work-types'), 'Failed to load work types');
  return extractItems<any>(json, ['work_types', 'terms']).map(normalizeTerm);
}

export async function createWorkType(term: { key: string; parent_key?: string; label: string; aliases: string[] }): Promise<WorkTypeTerm> {
  const payload: Record<string, any> = {
    key: term.key.trim(),
    label: term.label.trim(),
    aliases: term.aliases,
  };
  if (term.parent_key) payload.parent_key = term.parent_key;
  const json = await unwrapWithBody<any>(webApiClient.post<any>('/admin/work-types', payload), 'Failed to add work type');
  return normalizeTerm(json ?? payload);
}

/** The key is immutable — only label, aliases, and parent can change. */
export async function updateWorkType(
  key: string,
  updates: { label?: string; aliases?: string[]; parent_key?: string | null }
): Promise<WorkTypeTerm> {
  const json = await unwrapWithBody<any>(
    webApiClient.put<any>(`/admin/work-types/${encodeURIComponent(key)}`, updates),
    'Failed to update work type'
  );
  return normalizeTerm(json ?? { key, ...updates });
}

export async function setWorkTypeStatus(key: string, status: 'active' | 'deprecated'): Promise<void> {
  await unwrapWithBody<any>(
    webApiClient.post<any>(`/admin/work-types/${encodeURIComponent(key)}/status`, { status }),
    'Failed to change work type status'
  );
}

/** Re-runs matching everywhere; returns how many services got a suggestion. */
export async function recomputeWorkTypeSuggestions(opts?: { fuzzy?: boolean; threshold?: number }): Promise<{ suggested: number }> {
  // Exact alias pass by default; fuzzy=true follows it with a trigram
  // near-match pass (threshold 0..1). Both only ever write 'suggested'.
  const params = new URLSearchParams();
  if (opts?.fuzzy) params.set('fuzzy', 'true');
  if (opts?.threshold != null) params.set('threshold', String(opts.threshold));
  const qs = params.toString();
  const json = await unwrapWithBody<any>(
    webApiClient.post<any>(`/admin/work-types/recompute-suggestions${qs ? `?${qs}` : ''}`),
    'Failed to recompute suggestions'
  );
  return { suggested: Number(json?.suggested ?? 0) };
}

// Account-scoped recompute: a shop admin suggests over its OWN catalog.
export async function recomputeAccountSuggestions(
  accountId: string,
  opts?: { fuzzy?: boolean; threshold?: number }
): Promise<{ suggested: number }> {
  const params = new URLSearchParams();
  if (opts?.fuzzy) params.set('fuzzy', 'true');
  if (opts?.threshold != null) params.set('threshold', String(opts.threshold));
  const qs = params.toString();
  const json = await unwrapWithBody<any>(
    webApiClient.post<any>(`/accounts/${encodeURIComponent(accountId)}/work-types/recompute-suggestions${qs ? `?${qs}` : ''}`),
    'Failed to recompute suggestions'
  );
  return { suggested: Number(json?.suggested ?? 0) };
}

// Bulk confirm every suggested service in the account (optionally one location).
export async function confirmSuggestedWorkTypes(accountId: string, locationId?: string): Promise<{ confirmed: number }> {
  const qs = locationId ? `?location_id=${encodeURIComponent(locationId)}` : '';
  const json = await unwrapWithBody<any>(
    webApiClient.post<any>(`/accounts/${encodeURIComponent(accountId)}/work-types/confirm-suggested${qs}`),
    'Failed to confirm suggestions'
  );
  return { confirmed: Number(json?.confirmed ?? 0) };
}

/**
 * Label one service. `work_type_key` is required for confirmed/suggested and
 * omitted for excluded/unlabelled. Requires services:update (403 body carries
 * error/code — surfaced via unwrapWithBody).
 */
export async function setServiceWorkType(
  locationId: string,
  serviceId: string,
  state: ServiceWorkTypeState,
  workTypeKey?: string
): Promise<{ work_type_key?: string; work_type_state?: ServiceWorkTypeState; label?: string }> {
  const payload: Record<string, any> = { state };
  if (workTypeKey && (state === 'confirmed' || state === 'suggested')) payload.work_type_key = workTypeKey;
  const json = await unwrapWithBody<any>(
    webApiClient.put<any>(`/locations/${encodeURIComponent(locationId)}/services/${encodeURIComponent(serviceId)}/work-type`, payload),
    'Failed to update work type'
  );
  return json ?? {};
}

export async function getWorkTypeProgress(accountId: string): Promise<WorkTypeProgress> {
  const json = await unwrapWithBody<any>(
    webApiClient.get<any>(`/accounts/${encodeURIComponent(accountId)}/work-type-progress`),
    'Failed to load work type progress'
  );
  return {
    unlabelled: Number(json?.unlabelled ?? 0),
    suggested: Number(json?.suggested ?? 0),
    confirmed: Number(json?.confirmed ?? 0),
    excluded: Number(json?.excluded ?? 0),
  };
}

// ---------------------------------------------------------------------------
// Aggregate
// ---------------------------------------------------------------------------

export const workTypesAdmin = {
  listWorkTypes,
  listAllWorkTypes,
  createWorkType,
  updateWorkType,
  setWorkTypeStatus,
  recomputeWorkTypeSuggestions,
  setServiceWorkType,
  getWorkTypeProgress,
};
